import { DashboardLayout } from "./Dashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

const LAST_UPDATED = "November 24, 2025";

type Section = {
  id: string;
  title: string;
  body?: string[];
  bullets?: string[];
  subsections?: { heading: string; body: string[] }[];
};

const sections: Section[] = [
  {
    id: "information-collected",
    title: "1. Types of Information We Collect",
    subsections: [
      {
        heading: "1.1 Personal Information",
        body: [
          "Providers and designees: name, specialty, email, phone number, business address.",
          "Patients: name, date of birth, contact information, and other details provided during registration or communication via our Services.",
          "Authorized caregivers: name, relationship to the patient, email, and phone number.",
        ],
      },
      {
        heading: "1.2 Protected Health Information (PHI)",
        body: [
          "PHI includes any past, present, or future physical or mental health states; healthcare provision details; billing or payment information for healthcare services; and any data that could identify a patient.",
        ],
      },
      {
        heading: "1.3 Non-Personal / Non-Identifiable Information",
        body: [
          "Browser and device data, traffic data such as IP address and browsing behavior, cookies, pixel tags, web beacons, mobile device data (device ID, carrier, optional geolocation), and aggregated/anonymized analytics.",
        ],
      },
    ],
  },
  {
    id: "use-of-information",
    title: "2. How We Use Your Information",
    body: [
      "Provide and operate the Services, enable registration, access, and maintain the patient record.",
      "Respond to inquiries, communicate important updates, and send administrative messages.",
      "Support business purposes such as data analysis, audits, feature development, and performance reporting.",
      "Comply with legal obligations, protect user rights, ensure platform security, and fulfill HIPAA-mandated treatment, payment, and healthcare operations.",
      "Generate de-identified data for business purposes, research, and third-party sharing.",
    ],
  },
  {
    id: "disclosure",
    title: "3. Disclosure & Sharing of Information",
    body: [
      "With healthcare providers, designees, and authorized caregivers for treatment or coordination.",
      "With third-party service providers handling hosting, analytics, infrastructure, email, backups, or support—subject to confidentiality and BAAs where PHI is involved.",
      "During business transactions (mergers, acquisitions) under confidentiality obligations.",
      "To comply with legal or regulatory requirements.",
      "Sharing de-identified or aggregated data without restriction.",
    ],
  },
  {
    id: "phi-hipaa",
    title: "4. Use and Protection of PHI (HIPAA)",
    body: [
      "Anthro complies with HIPAA/HITECH, enters BAAs with partners handling PHI, and limits PHI use to treatment, payment, and healthcare operations unless additional authorization is obtained.",
      "Users may revoke authorizations in writing, though prior uses remain valid. Third parties are contractually obliged to protect PHI, but we cannot guarantee absolute prevention of re-disclosure.",
    ],
  },
  {
    id: "tracking",
    title: "5. Non-Personal Information (Cookies, Tracking & Analytics)",
    body: [
      "We use session and persistent cookies, web beacons, and similar technologies to improve experiences. Disabling cookies may limit functionality.",
      "Our mobile apps may collect device identifiers, OS details, and optional location data.",
      "Third-party analytics or advertising services may place their own cookies or pixels.",
    ],
  },
  {
    id: "security",
    title: "6. Security",
    body: [
      "We maintain administrative, technical, and physical safeguards aligned with HIPAA standards, including encryption in transit/at rest, role-based access control, secure development practices, 2FA (where available), audits, vulnerability scanning, intrusion detection, backups, and incident response procedures.",
    ],
  },
  {
    id: "user-rights",
    title: "7. Accessing, Correcting, and Deleting Information",
    body: [
      "Users can request access, corrections, or deletion of Personal Information/PHI by contacting us. Certain deletions may require account termination, and some data may persist in backups or logs for compliance.",
    ],
  },
  {
    id: "retention",
    title: "8. Data Retention",
    body: [
      "We retain data only as long as necessary to provide Services, meet legal obligations, resolve disputes, or enforce agreements, after which data is securely deleted or de-identified.",
    ],
  },
  {
    id: "international-transfers",
    title: "9. International Transfers",
    body: [
      "Data may be stored and processed in the United States. For users outside the U.S., we implement appropriate safeguards when transferring data across borders (e.g., GDPR compliance).",
    ],
  },
  {
    id: "minors",
    title: "10. Use by Minors",
    body: [
      "Services are not intended for individuals under 18 without parental/guardian consent. Unauthorized data collected from minors will be deleted upon discovery.",
    ],
  },
  {
    id: "changes",
    title: "11. Changes to This Privacy Policy",
    body: [
      "We may update this policy periodically. The “Last Updated” date reflects changes, and significant updates may be communicated via email or in-app notifications. Continued use signifies acceptance.",
    ],
  },
  {
    id: "contact",
    title: "12. Contact Us",
    body: [
      "Anthro Care Inc.",
      "30 N Gould St Ste 100",
      "Sheridan, WY 82801, USA",
      "support@anthro.care",
    ],
  },
  {
    id: "legal-basis",
    title: "13. Legal Basis (GDPR / Data Protection Law)",
    body: [
      "Consent for marketing or non-core PHI uses.",
      "Contractual necessity to provide the Services.",
      "Legal obligation to meet regulatory requirements.",
      "Legitimate interests for operational needs balanced against user rights.",
    ],
  },
  {
    id: "hipaa-terms",
    title: "14. Business Associate / HIPAA Terms",
    body: [
      "We sign BAAs with vendors accessing PHI and limit usage to treatment, payment, or healthcare operations, except when you authorize otherwise. We comply with HIPAA breach notification rules.",
    ],
  },
  {
    id: "security-audit",
    title: "15. Security & Certification / Audit",
    body: [
      "We follow industry standards for encryption, access control, and secure coding (OWASP). Regular penetration tests, audits, an ISMS, redundancy, and disaster recovery procedures support our security posture.",
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <DashboardLayout activePage="/privacy">
      <div className="space-y-6 sm:space-y-8">
        <header className="space-y-4">
          <Badge variant="secondary" className="text-xs uppercase tracking-wide">
            Legal
          </Badge>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Privacy Policy
            </h1>
            <p className="text-muted-foreground mt-2 max-w-3xl">
              Learn how Anthro Care Inc. collects, uses, and protects your
              information when you use our healthcare platform.
            </p>
          </div>
          <div className="text-sm text-muted-foreground">
            Last Updated: <span className="font-medium">{LAST_UPDATED}</span>
          </div>
        </header>

        <div className="grid gap-6 lg:grid-cols-[260px,1fr]">
          <Card className="h-fit">
            <CardHeader>
              <CardTitle className="text-base">On This Page</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="block rounded-md px-3 py-2 text-muted-foreground transition hover:bg-muted hover:text-foreground"
                >
                  {section.title}
                </a>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-8 pt-6">
              <ScrollArea className="h-[70vh] pr-4">
                <div className="space-y-8">
                  {sections.map((section) => (
                    <section
                      key={section.id}
                      id={section.id}
                      className="space-y-3"
                    >
                      <h2 className="text-xl font-semibold">{section.title}</h2>
                      {section.body?.map((paragraph) => (
                        <p
                          key={paragraph}
                          className="text-sm leading-relaxed text-muted-foreground"
                        >
                          {paragraph}
                        </p>
                      ))}
                      {section.bullets && (
                        <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
                          {section.bullets.map((bullet) => (
                            <li key={bullet}>{bullet}</li>
                          ))}
                        </ul>
                      )}
                      {section.subsections?.map((sub) => (
                        <div
                          key={sub.heading}
                          className="space-y-2 rounded-md border-l-2 border-muted pl-4"
                        >
                          <h3 className="text-sm font-semibold">{sub.heading}</h3>
                          {sub.body.map((paragraph) => (
                            <p
                              key={paragraph}
                              className="text-sm text-muted-foreground"
                            >
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      ))}
                      <Separator />
                    </section>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}

